import { FieldType } from '@databases/model/field';
import {
  type JiraIssueValue,
  type MutableJiraIssueValue,
  type SingleJiraIssueValue,
  type ValueJSON
} from '@databases/model/value';
import * as Y from 'yjs';

import { SynchronizedArray } from '../helpers/synchronized-array';
import { SynchronizedMapElement } from '../helpers/synchronized-map-element';
import { SynchronizedRecord } from '../helpers/synchronized-record';
import { type DisposableElement, type WrappedYType } from '../helpers/types';
import { ValueProperties } from '../y-entry';
import { FieldMapping } from '../y-field';

const JiraIssueValueProperties = {
  key: 'k',
  id: 'i'
};

class YSingleJiraIssueValue extends SynchronizedRecord {
  static mapper(value: unknown): YSingleJiraIssueValue | undefined {
    return value instanceof Y.Map ? new YSingleJiraIssueValue(value) : undefined;
  }

  get key(): string {
    return this.getString(JiraIssueValueProperties.key, '');
  }

  set key(val: string) {
    this.set(JiraIssueValueProperties.key, val);
  }

  get id(): string {
    return this.getString(JiraIssueValueProperties.id, '');
  }

  toJSON(): SingleJiraIssueValue {
    return {
      key: this.key,
      id: this.id
    };
  }

  static fromJson(value: SingleJiraIssueValue): YSingleJiraIssueValue {
    return new YSingleJiraIssueValue(
      new Y.Map([
        [JiraIssueValueProperties.key, value.key],
        [JiraIssueValueProperties.id, value.id]
      ])
    );
  }
}

export class YJiraIssueValue
  implements JiraIssueValue, MutableJiraIssueValue, WrappedYType, DisposableElement
{
  private valueElement: SynchronizedMapElement<SynchronizedArray<YSingleJiraIssueValue>>;

  constructor(private input: Y.Map<any>) {
    this.valueElement = this.buildValueElement();
  }

  private buildValueElement(): SynchronizedMapElement<
    SynchronizedArray<YSingleJiraIssueValue>
  > {
    return new SynchronizedMapElement<SynchronizedArray<YSingleJiraIssueValue>>(
      this.input,
      ValueProperties.value,
      (yarray) => {
        if (yarray instanceof Y.Array) {
          return new SynchronizedArray(
            yarray as Y.Array<unknown>,
            YSingleJiraIssueValue.mapper
          );
        }
      },
      () => new Y.Array()
    );
  }

  get isEmpty() {
    return !this.valueElement.value?.toArray().length;
  }

  get type(): FieldType.JiraIssue {
    return FieldType.JiraIssue;
  }

  get value(): YSingleJiraIssueValue[] {
    return this.valueElement.value?.toArray() ?? [];
  }

  add(value: SingleJiraIssueValue): void {
    this.valueElement.valueRequired.push([YSingleJiraIssueValue.fromJson(value)]);
  }

  delete(index: number): void {
    this.valueElement.valueRequired.delete(index);
  }

  reorder(index: number, newIndex: number): void {
    const val = this.valueElement.valueRequired.get(index).toJSON();
    this.valueElement.valueRequired.delete(index);
    this.valueElement.valueRequired.insert(newIndex, [
      YSingleJiraIssueValue.fromJson(val)
    ]);
  }

  setTo(value: SingleJiraIssueValue[]): void {
    const valueRef = new Y.Array();
    this.input.set(ValueProperties.value, valueRef);
    this.valueElement = this.buildValueElement();

    this.valueElement.valueRequired.push(
      value.map((v) => YSingleJiraIssueValue.fromJson(v))
    );
  }

  toJSON(): ValueJSON<FieldType.JiraIssue, SingleJiraIssueValue[]> {
    return {
      type: this.type,
      value: this.value.map((v) => v.toJSON())
    };
  }

  static fromJSON(json: ValueJSON<FieldType.JiraIssue, SingleJiraIssueValue[]>) {
    return new YJiraIssueValue(
      new Y.Map([
        [ValueProperties.type, FieldMapping[json.type]],
        [
          ValueProperties.value,
          Y.Array.from(json.value.map((v) => YSingleJiraIssueValue.fromJson(v).unwrap()))
        ]
      ])
    );
  }

  unwrap(): Y.AbstractType<any> {
    return this.input;
  }

  dispose(): void {
    this.valueElement.dispose();
  }
}
